/**
 * MafiaBoard — Main game area with phase header, timer, panels, chat, player list, event log
 *
 * Requirements: 8.1, 8.3, 8.6, 19.1–19.4, 26.1, 26.4–26.6
 */

import React from 'react';
import {
  PlayerVisibleState,
  GamePhaseType,
  MafiaRole,
  ChatMessage,
  GameEvent,
  InvestigationResult,
} from '../types';
import { MafiaChat } from './MafiaChat';
import { MafiaVoting } from './MafiaVoting';
import { MafiaNightAction } from './MafiaNightAction';
import { MafiaPlayerList } from './MafiaPlayerList';
import { MafiaEventLog } from './MafiaEventLog';
import { MafiaRoleReminder } from './MafiaRoleReminder';
import './MafiaBoard.css';

export interface MafiaBoardProps {
  gameState: PlayerVisibleState;
  currentPhase: GamePhaseType;
  playerId: string;
  playerRole: MafiaRole;
  onMafiaVote: (targetId: string) => void;
  onDoctorProtect: (targetId: string) => void;
  onDetectiveInvestigate: (targetId: string) => void;
  onDayVote: (targetId: string | null) => void;
  onChatMessage: (message: string) => void;
  phaseTimeRemaining: number;
  investigationResults?: InvestigationResult[];
  myNightSelection?: string;
  myDayVote?: string | null;
}

export const MafiaBoard: React.FC<MafiaBoardProps> = ({
  gameState,
  currentPhase,
  playerId,
  playerRole,
  onMafiaVote,
  onDoctorProtect,
  onDetectiveInvestigate,
  onDayVote,
  onChatMessage,
  phaseTimeRemaining,
  investigationResults,
  myNightSelection,
  myDayVote,
}) => {
  const isNight = currentPhase === 'night';
  const isDay = currentPhase === 'dayDiscussion' || currentPhase === 'dayVoting';
  const isVoting = currentPhase === 'dayVoting';
  const isDiscussion = currentPhase === 'dayDiscussion';

  const alivePlayers = gameState.players.filter((p) => p.status === 'alive');
  const myPlayer = gameState.players.find((p) => p.id === playerId);
  const isDead = myPlayer?.status === 'dead';

  // Phase display name
  const getPhaseDisplayName = (): string => {
    const phase = gameState.phase;
    switch (phase.type) {
      case 'night': return `🌙 Night ${phase.nightNumber}`;
      case 'dayDiscussion': return `☀️ Day ${phase.dayNumber} — Discussion`;
      case 'dayVoting': return `🗳️ Day ${phase.dayNumber} — Voting`;
      default: return '';
    }
  };

  // Timer formatting
  const formatTimer = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const timerWarning = phaseTimeRemaining <= 10;

  // Chat state
  const isChatDisabled = isDead || isVoting || (isNight && playerRole !== 'mafia');
  const chatChannel = isNight && playerRole === 'mafia' ? 'mafia-night' as const : 'day' as const;
  const chatMessages: ChatMessage[] = chatChannel === 'mafia-night'
    ? (gameState.mafiaChatMessages || [])
    : gameState.dayChatMessages;

  // Night action state
  const mafiaVotes = gameState.mafiaVotes || new Map<string, string>();
  const lastProtectedPlayerId = gameState.lastProtectedPlayerId;
  const latestInvestigation = gameState.investigationResult;

  // Voting state
  const currentVotes = gameState.votes || new Map<string, string | null>();

  // Build alive MafiaPlayer-like objects for night action (need role for mafia filtering)
  const alivePlayersForNight = alivePlayers.map((p) => ({
    id: p.id,
    name: p.name,
    role: p.role || ('villager' as const),
    team: (p.role === 'mafia' ? 'mafia' : 'village') as 'village' | 'mafia',
    status: p.status,
    connectionStatus: p.connectionStatus,
    isHost: p.isHost,
    disconnectedPhases: 0,
  }));

  return (
    <div className="mafia-board">
      {/* Phase Header */}
      <div
        className={`mafia-board__phase-header ${isNight ? 'mafia-board__phase-header--night' : ''} ${isDay ? 'mafia-board__phase-header--day' : ''}`}
      >
        <div className="mafia-board__phase-name">{getPhaseDisplayName()}</div>
        <div className={`mafia-board__timer ${timerWarning ? 'mafia-board__timer--warning' : ''}`} role="timer" aria-label={`${Math.floor(phaseTimeRemaining)} seconds remaining`}>
          {formatTimer(phaseTimeRemaining)}
        </div>
      </div>

      {/* Content: Main + Sidebar */}
      <div className="mafia-board__content">
        <div className="mafia-board__main">
          {/* Night Action Panel */}
          {isNight && (
            <MafiaNightAction
              role={playerRole}
              alivePlayers={alivePlayersForNight}
              mafiaVotes={mafiaVotes}
              lastProtectedPlayerId={lastProtectedPlayerId}
              onMafiaVote={onMafiaVote}
              onDoctorProtect={onDoctorProtect}
              onDetectiveInvestigate={onDetectiveInvestigate}
              investigationResult={latestInvestigation}
              disabled={isDead}
              myPlayerId={playerId}
              mySelection={myNightSelection}
            />
          )}

          {/* Voting Panel */}
          {isVoting && (
            <MafiaVoting
              alivePlayers={alivePlayersForNight}
              currentVotes={currentVotes}
              playerId={playerId}
              onVote={onDayVote}
              myCurrentVote={myDayVote ?? null}
              disabled={isDead}
            />
          )}

          {/* Chat */}
          <MafiaChat
            messages={chatMessages}
            onSendMessage={onChatMessage}
            disabled={isChatDisabled}
            channel={chatChannel}
            playerName={myPlayer?.name || 'Player'}
          />
        </div>

        {/* Sidebar */}
        <div className="mafia-board__sidebar">
          <MafiaPlayerList
            players={gameState.players}
            currentPlayerId={playerId}
          />
          <MafiaEventLog events={gameState.eventLog} />
        </div>
      </div>

      {/* Role Reminder */}
      <div className="mafia-board__role-reminder">
        <MafiaRoleReminder
          role={playerRole}
          team={gameState.myTeam}
          currentPhase={currentPhase}
          mafiaMembers={gameState.mafiaMembers}
          investigationResults={investigationResults}
          lastProtectedPlayer={lastProtectedPlayerId}
        />
      </div>
    </div>
  );
};

export default MafiaBoard;
